/** 与官网订阅页、充值弹窗共用的套餐定义（金额需与支付网关一致） */
export type RechargePlanId = 'flex' | 'starter' | 'pro' | 'flagship' | 'yearly';

/** 权益行：左侧说明 + 可选右侧单次积分 */
export interface PlanFeatureRow {
  main: { zh: string; en: string };
  unit?: { zh: string; en: string };
  /** 默认对勾；clock 用于半年度会员「积分期限」等说明 */
  leadingIcon?: 'check' | 'clock';
}

export interface RechargePlan {
  id: RechargePlanId;
  amount: number;
  /** 划线原价（仅展示，如半年度会员 ¥1800） */
  listPrice?: number;
  credits: number;
  expireDays: number;
  titles: { zh: string; en: string };
  /** 积分区下方金色小字，无则省略 */
  bonusHint?: { zh: string; en: string };
  featureRows: PlanFeatureRow[];
  primaryCta: { zh: string; en: string };
  /** white: 白底黑字； gold: 金底黑字（旗舰） */
  ctaStyle: 'white' | 'gold';
  badge?: { zh: string; en: string };
  /** 金边高亮 */
  cardHighlight?: boolean;
  /** 为 true 时不出现在充值卡片网格（仍保留 id 供旧链接/数据兼容） */
  hideInPlanCards?: boolean;
}

const U15: PlanFeatureRow['unit'] = { zh: '15积分/张', en: '15 pts/image' };
const U10: PlanFeatureRow['unit'] = { zh: '10积分/张', en: '10 pts/image' };
const U9: PlanFeatureRow['unit'] = { zh: '9积分/张', en: '9 pts/image' };
const U20: PlanFeatureRow['unit'] = { zh: '20积分/张', en: '20 pts/image' };
const U12: PlanFeatureRow['unit'] = { zh: '12积分/张', en: '12 pts/image' };

const FLAGSHIP_ROWS: PlanFeatureRow[] = [
  { main: { zh: '单项：300 张灯光图', en: 'Single item: 300 lighting maps' }, unit: U20 },
  { main: { zh: '单项：300 张文字修改生图', en: 'Single item: 300 text-edit images' }, unit: U20 },
  { main: { zh: '单项：500 张AI高清放大', en: 'Single item: 500 AI HD enlargements' }, unit: U12 },
  { main: { zh: '单项：500 张光影映射', en: 'Single item: 500 light/shadow mappings' }, unit: U12 },
  { main: { zh: '单项：500 张光影重构', en: 'Single item: 500 light/shadow reconstruction' }, unit: U12 },
  { main: { zh: '单项：500 张文本重构', en: 'Single item: 500 text reconstructions' }, unit: U12 },
  { main: { zh: '单项：500 张物理重构', en: 'Single item: 500 physical reconstructions' }, unit: U12 },
  {
    main: {
      zh: '积分将在购买/续费起60天后清零。',
      en: 'Points expire 60 days after purchase/renewal.',
    },
  },
  { main: { zh: '水印生成', en: 'Watermarked output' } },
];

const FLEX_ROWS: PlanFeatureRow[] = [
  { main: { zh: '单项：10 张灯光图', en: 'Single item: 10 lighting maps' }, unit: U15 },
  { main: { zh: '单项：10 张文字修改生图', en: 'Single item: 10 text-edit images' }, unit: U15 },
  { main: { zh: '单项：15 张AI高清放大', en: 'Single item: 15 AI HD enlargements' }, unit: U10 },
  { main: { zh: '单项：15 张光影映射', en: 'Single item: 15 light/shadow mappings' }, unit: U10 },
  { main: { zh: '单项：15 张光影重构', en: 'Single item: 15 light/shadow reconstruction' }, unit: U10 },
  { main: { zh: '单项：15 张文本重构', en: 'Single item: 15 text reconstructions' }, unit: U10 },
  { main: { zh: '单项：15 张物理重构', en: 'Single item: 15 physical reconstructions' }, unit: U10 },
  {
    main: {
      zh: '积分将在购买/续费起30天后清零。',
      en: 'Points expire 30 days after purchase/renewal.',
    },
  },
  { main: { zh: '水印生成', en: 'Watermarked output' } },
];

const STARTER_ROWS: PlanFeatureRow[] = [
  { main: { zh: '单项：40 张灯光图', en: 'Single item: 40 lighting maps' }, unit: U15 },
  { main: { zh: '单项：40 张文字修改生图', en: 'Single item: 40 text-edit images' }, unit: U15 },
  { main: { zh: '单项：60 张AI高清放大', en: 'Single item: 60 AI HD enlargements' }, unit: U10 },
  { main: { zh: '单项：60 张光影映射', en: 'Single item: 60 light/shadow mappings' }, unit: U10 },
  { main: { zh: '单项：60 张光影重构', en: 'Single item: 60 light/shadow reconstruction' }, unit: U10 },
  { main: { zh: '单项：60 张文本重构', en: 'Single item: 60 text reconstructions' }, unit: U10 },
  { main: { zh: '单项：60 张物理重构', en: 'Single item: 60 physical reconstructions' }, unit: U10 },
  {
    main: {
      zh: '积分将在购买/续费起30天后清零。',
      en: 'Points expire 30 days after purchase/renewal.',
    },
  },
  { main: { zh: '水印生成', en: 'Watermarked output' } },
];

const YEARLY_ROWS: PlanFeatureRow[] = [
  { main: { zh: '单项：1800 张灯光图', en: 'Single item: 1800 lighting maps' }, unit: U20 },
  { main: { zh: '单项：1800 张文字修改生图', en: 'Single item: 1800 text-edit images' }, unit: U20 },
  { main: { zh: '单项：3000 张AI高清放大', en: 'Single item: 3000 AI HD enlargements' }, unit: U12 },
  { main: { zh: '单项：3000 张光影映射', en: 'Single item: 3000 light/shadow mappings' }, unit: U12 },
  { main: { zh: '单项：3000 张光影重构', en: 'Single item: 3000 light/shadow reconstruction' }, unit: U12 },
  { main: { zh: '单项：3000 张文本重构', en: 'Single item: 3000 text reconstructions' }, unit: U12 },
  { main: { zh: '单项：3000 张物理重构', en: 'Single item: 3000 physical reconstructions' }, unit: U12 },
  {
    main: {
      zh: '积分在购买/续费起6个月后清零',
      en: 'Points expire 6 months after purchase/renewal.',
    },
    leadingIcon: 'clock',
  },
  { main: { zh: '水印生成', en: 'Watermarked output' } },
];

const PRO_ROWS: PlanFeatureRow[] = [
  { main: { zh: '单项：120 张灯光图', en: 'Single item: 120 lighting maps' }, unit: U15 },
  { main: { zh: '单项：120 张文字修改生图', en: 'Single item: 120 text-edit images' }, unit: U15 },
  { main: { zh: '单项：200 张AI高清放大', en: 'Single item: 200 AI HD enlargements' }, unit: U9 },
  { main: { zh: '单项：200 张光影映射', en: 'Single item: 200 light/shadow mappings' }, unit: U9 },
  { main: { zh: '单项：200 张光影重构', en: 'Single item: 200 light/shadow reconstruction' }, unit: U9 },
  { main: { zh: '单项：200 张文本重构', en: 'Single item: 200 text reconstructions' }, unit: U9 },
  { main: { zh: '单项：200 张物理重构', en: 'Single item: 200 physical reconstructions' }, unit: U9 },
  {
    main: {
      zh: '积分将在购买/续费起30天后清零。',
      en: 'Points expire 30 days after purchase/renewal.',
    },
  },
  { main: { zh: '水印生成', en: 'Watermarked output' } },
];

export const RECHARGE_PLANS: RechargePlan[] = [
  {
    id: 'flex',
    amount: 15,
    credits: 150,
    expireDays: 30,
    titles: { zh: '灵活充值', en: 'Flexible top-up' },
    featureRows: FLEX_ROWS,
    primaryCta: { zh: '立即充值', en: 'Recharge now' },
    ctaStyle: 'white',
  },
  {
    id: 'starter',
    amount: 60,
    credits: 600,
    expireDays: 30,
    titles: { zh: '入门版 (体验)', en: 'Starter (trial)' },
    featureRows: STARTER_ROWS,
    primaryCta: { zh: '立即充值', en: 'Recharge now' },
    ctaStyle: 'white',
  },
  {
    id: 'pro',
    amount: 150,
    credits: 1800,
    expireDays: 30,
    titles: { zh: '专业版 (PRO)', en: 'Pro' },
    bonusHint: { zh: '★ 含限时赠送 300 积分', en: '★ Includes limited-time 300 bonus points' },
    featureRows: PRO_ROWS,
    primaryCta: { zh: '开启 PRO', en: 'Start PRO' },
    ctaStyle: 'white',
  },
  {
    id: 'flagship',
    amount: 300,
    credits: 6000,
    expireDays: 60,
    titles: { zh: '旗舰版（旗舰）', en: 'Flagship' },
    bonusHint: { zh: '★ 含限时赠送 3000 积分', en: '★ Includes limited-time 3000 bonus points' },
    featureRows: FLAGSHIP_ROWS,
    primaryCta: { zh: '尊享旗舰权益', en: 'Flagship benefits' },
    ctaStyle: 'gold',
    badge: { zh: '最高性价比', en: 'Best value' },
    cardHighlight: true,
  },
];

/** 半年度会员（单独视图，不出现在月度四卡网格中；plan id 仍为 yearly 供支付兼容） */
export const YEARLY_MEMBERSHIP_PLAN: RechargePlan = {
  id: 'yearly',
  amount: 1680,
  listPrice: 1800,
  credits: 36000,
  expireDays: 180,
  titles: { zh: '半年度会员', en: 'Semi-annual membership' },
  bonusHint: {
    zh: '★ 半年度特惠 立省¥120（相当于旗舰版6次9.3折）',
    en: '★ Semi-annual special: save ¥120 — like 9.3% off six flagship bundles',
  },
  featureRows: YEARLY_ROWS,
  primaryCta: { zh: '开通半年度会员', en: 'Open semi-annual membership' },
  ctaStyle: 'gold',
  /** 与 ¥300 旗舰版一致的金色描边 */
  cardHighlight: true,
  hideInPlanCards: true,
};

export function getRechargePlanById(id: RechargePlanId): RechargePlan | undefined {
  if (id === 'yearly') return YEARLY_MEMBERSHIP_PLAN;
  return RECHARGE_PLANS.find((p) => p.id === id);
}
